import { initializeTracing } from '@imprivata-cloud/common';
import type { TracerProvider } from '@opentelemetry/api';
import { StorageKeys } from '../api/constants';
import { getApiUrl } from '../api/utils';
import { isSetup } from './utils';

interface Tracing {
  serviceName: string;
  getTracer: () => void;
  getProvider: () => TracerProvider;
  getWorkflowId: () => string;
  updateWorkflowId: (id: string) => void;
}

export let tracer: Tracing;

//  if page is refreshed in the middle of the setup, you keep the workflowId
if (getWorkflowId() && isSetup()) {
  initTracerSetup(getWorkflowId());
}

export function getWorkflowId(): string {
  return sessionStorage.getItem(StorageKeys.WORKFLOW_ID) || '';
}

export function initTracerSetup(workflowId: string) {
  initTracer('setup-ui', workflowId);
}

export function initTracerPortal(workflowId: string) {
  initTracer('portal-ui', workflowId);
}

// The fetch/XHR auto-instrumentation patches globalThis.fetch, and the OTLP
// exporter posts spans over that same fetch. Its only guard against tracing a
// request is ignoreUrls — it does not honour suppressTracing — so without this
// the export POST is itself traced, SimpleSpanProcessor flushes that span at
// once, and every export begets another: a self-sustaining trace loop. Matched
// as a substring regex so it holds whatever host getApiUrl() resolves to.
const OTLP_TRACES_PATH = /\/v1\/traces(\?|$)/;

export function initTracer(serviceName: string, workflowId: string) {
  // Setup is a process-wide side effect: it patches globalThis.fetch and
  // registers a global provider. It MUST run once. Session (its caller) is
  // keyed by path and remounts on every in-app navigation, so a naive re-init
  // stacks another fetch-instrumentation layer over the last (the SDK's
  // re-patch guard is per-instance, and each call builds a fresh instance) and
  // leaks a TracerProvider — after which every request exports N duplicate
  // spans. Later calls therefore only refresh the workflow id on the live
  // tracer rather than building another one.
  if (tracer) {
    tracer.updateWorkflowId(workflowId);
    return;
  }
  const apiUrl = getApiUrl();
  tracer = initializeTracing({
    serviceName,
    workflowId,
    OTLP_TRACING_URL: `${apiUrl}/v1/traces`,
    ignoreUrls: [OTLP_TRACES_PATH],
  });
}
